import cn from "classnames";
import * as R from "ramda";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Overlay } from "react-bootstrap";
import { MdClose, MdMoreHoriz } from "react-icons/md";
import { Button, ButtonProps, ButtonType } from "../Button/Button";
import { ButtonFrameworkVariant } from "../ButtonFramework/ButtonFramework";
import { MenuItem } from "../Dropdown/Dropdown";
import { DropdownMenu } from "../DropdownMenu/DropdownMenu";

export const SidePanelDivider = (): JSX.Element => <div className="sectionDivider" />;

interface SidePanelSectionHeaderProps {
  icon: JSX.Element;
  text: string;
  onClick?: () => void;
  className?: string;
}

export const SidePanelSectionHeader = ({
  icon,
  text,
  onClick,
  className = "",
}: SidePanelSectionHeaderProps): JSX.Element => {
  return (
    <Button
      className={cn("sectionHeader", {
        [className]: className,
        noninteractive: onClick === undefined,
      })}
      type={ButtonType.EMPTY}
      variant={ButtonFrameworkVariant.LEADING_ICON}
      design="secondary"
      icon={icon}
      onClick={onClick}
    >
      {text}
    </Button>
  );
};

export interface SidePanelListItemAction {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
}

interface SidePanelListItemProps {
  text: string | React.ReactNode;
  actions?: SidePanelListItemAction[];
  passthroughProps?: Partial<ButtonProps>;
  showActionsMenu: boolean;
  setShowActionsMenu: (show: boolean) => void;
  actionsIconReplacement?: JSX.Element;
}

export const SidePanelListItem = ({
  text,
  actions,
  passthroughProps,
  showActionsMenu,
  setShowActionsMenu,
  actionsIconReplacement,
}: SidePanelListItemProps): JSX.Element => {
  const actionsMap = useMemo(() => R.indexBy(R.prop("label"), actions ?? []), [actions]);
  const menuItems: MenuItem<string>[] = useMemo(
    () => actions?.map(action => ({ leadingContent: action.icon, value: action.label })) ?? [],
    [actions]
  );
  const overlayTarget = useRef<HTMLDivElement>(null);

  const actionsIcon = actionsIconReplacement ?? (
    <MdMoreHoriz
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        setShowActionsMenu(!showActionsMenu);
      }}
      className="listItem-openActionsMenu"
    />
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const menuEl = document.querySelector("#sidePanelActionsMenu");
      const contains =
        menuEl?.contains(event.target as Node) ||
        overlayTarget.current?.contains(event.target as Node);

      if (!contains) {
        setShowActionsMenu(false);
      }
    },
    [setShowActionsMenu]
  );

  useEffect(() => {
    if (showActionsMenu) {
      document.body.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.body.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside, showActionsMenu]);

  return (
    <>
      <Button
        className="listItem"
        type={ButtonType.EMPTY}
        variant={ButtonFrameworkVariant.TRAILING_ICON}
        design="secondary"
        icon={<div ref={overlayTarget}>{actionsIcon}</div>}
        {...passthroughProps}
      >
        {text}
      </Button>
      <Overlay target={overlayTarget} show={showActionsMenu} placement="bottom-start">
        <div id="sidePanelActionsMenu">
          <DropdownMenu
            menuItems={menuItems}
            itemOnClick={({ value }) => {
              actionsMap[value]?.onClick();
              setShowActionsMenu(false);
            }}
            standalone
          />
        </div>
      </Overlay>
    </>
  );
};

interface SidePanelProps {
  title?: React.ReactNode;
  rightActions?: React.ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  show: boolean;
  onHide: () => void;
}

export const SidePanel = ({
  title,
  rightActions,
  body,
  footer,
  show,
  onHide,
}: SidePanelProps): JSX.Element => {
  return (
    <div className={cn("sidePanelContainer", { open: show, closed: !show })}>
      <div className="sidePanel">
        <div className="sidePanelHeader">
          <div className="sidePanelTitle">{title}</div>
          <div className="sidePanelRightActions">
            {rightActions}
            <div className="sidePanelAction">
              <Button
                type={ButtonType.EMPTY}
                variant={ButtonFrameworkVariant.ICON_ONLY}
                size="sm"
                design="secondary"
                icon={<MdClose />}
                onClick={onHide}
              />
            </div>
          </div>
        </div>
        <div className="sidePanelBody">{body}</div>
        {footer && <div className="sidePanelFooter">{footer}</div>}
      </div>
    </div>
  );
};

export default SidePanel;
